import { useEffect, useCallback, useRef } from 'react';
import { useSuspenseQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import {
  QUERY_FETCH_PODCAST_DETAIL_PAGE,
  QUERY_FETCH_PODCAST_LIST_PAGE,
  QUERY_PODCAST_BY_SLUG,
  PodcastDetailPageQuery,
  PodcastListPageQuery,
  PodcastBySlugQuery,
  PodcastBySlugQueryVariables,
  PodcastListeningTime,
  PodcastListeningTimeInput,
  PodcastDetailPage,
  PodcastListPage,
  Podcast,
} from '@edwin-group/people-graphql';
import { usePodcastDetailStore } from '../store/usePodcastDetailStore';
import { usePodcastListStore } from '@/pages/podcast/list/store/usePodcastListStore';
import { getAuthentication } from '@/hooks/useAuthentication';
import { createPodcastListeningTime, updatePodcastListeningTime } from '@/services/podcast/podcast.service';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';

export const usePodcastDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = getAuthentication();

  const podcastListPageFromStore = usePodcastListStore((state) => state.page);

  const { data: pageData } = useSuspenseQuery<PodcastDetailPageQuery>(QUERY_FETCH_PODCAST_DETAIL_PAGE);
  const { data: listPageData } = useSuspenseQuery<PodcastListPageQuery>(QUERY_FETCH_PODCAST_LIST_PAGE, {
    skip: !!podcastListPageFromStore,
  });

  const { data: podcastData } = useSuspenseQuery<PodcastBySlugQuery, PodcastBySlugQueryVariables>(
    QUERY_PODCAST_BY_SLUG,
    {
      variables: {
        slug: slug || '',
        userDocumentId: user?.documentId || '',
      },
      skip: !slug,
    },
  );

  const setPage = usePodcastDetailStore((state) => state.setPage);
  const setCurrent = usePodcastDetailStore((state) => state.setCurrent);
  const setPodcastListPage = usePodcastDetailStore((state) => state.setPodcastListPage);
  const setListeningTime = usePodcastDetailStore((state) => state.setListeningTime);
  const listeningTime = usePodcastDetailStore((state) => state.listeningTime);
  const current = usePodcastDetailStore((state) => state.current);
  const reset = usePodcastDetailStore((state) => state.reset);

  const lastSentTimeRef = useRef<{
    documentId: string | null;
    id: string | null;
    listeningTime: number;
    duration: number;
  }>({
    documentId: null,
    id: null,
    listeningTime: 0,
    duration: 0,
  });

  useEffect(() => {
    if (pageData?.podcastDetailPage) {
      setPage(pageData.podcastDetailPage as PodcastDetailPage);
    }
  }, [pageData, setPage]);

  useEffect(() => {
    const podcastListPage = podcastListPageFromStore || listPageData?.podcastListPage || null;
    if (podcastListPage) {
      setPodcastListPage(podcastListPage as PodcastListPage);
    }
  }, [podcastListPageFromStore, listPageData, setPodcastListPage]);

  useEffect(() => {
    reset();
  }, [slug, reset]);

  useEffect(() => {
    if (!podcastData?.podcastBySlug) return;

    const { current: currentPodcast } = podcastData.podcastBySlug;

    if (currentPodcast) {
      setCurrent(currentPodcast as Podcast);

      if (currentPodcast.listenings?.length) {
        const listening = currentPodcast.listenings[0];
        if (listening) {
          const progress = Math.ceil((listening.listeningTime / listening.duration) * 100);
          const isDone = progress >= 90;

          lastSentTimeRef.current = {
            documentId: listening.documentId || null,
            id: listening.documentId || null,
            listeningTime: isDone ? 0 : listening.listeningTime,
            duration: listening.duration,
          };
          setListeningTime(lastSentTimeRef.current);
        }
      } else {
        lastSentTimeRef.current = { documentId: null, id: null, listeningTime: 0, duration: 0 };
        setListeningTime(lastSentTimeRef.current);
      }

      trackGAEvent('podcast_played', {
        podcast_document_id: currentPodcast.documentId ?? '',
        podcast_name: currentPodcast.title ?? '',
      });
    }
  }, [podcastData, setCurrent, setListeningTime]);

  const handleTimeUpdate = useCallback(
    async (audio: HTMLAudioElement) => {
      const currentTime = Math.floor(audio.currentTime);
      const duration = Math.floor(audio.duration);

      if (!lastSentTimeRef.current.id) {
        const inputData: PodcastListeningTimeInput = {
          duration,
          listeningTime: currentTime,
          podcast: current?.documentId || '',
          staff: user?.documentId,
        };

        const { data, error } = await createPodcastListeningTime(inputData);

        if (error) {
          console.error('Error creating podcast listening time', error);
          return;
        }

        if (data?.createPodcastListeningTime) {
          const newListening = data.createPodcastListeningTime as PodcastListeningTime;
          lastSentTimeRef.current = {
            documentId: newListening.documentId || null,
            id: newListening.documentId || null,
            listeningTime: newListening.listeningTime,
            duration: newListening.duration,
          };
          setListeningTime(lastSentTimeRef.current);
        }
        return;
      }

      if (lastSentTimeRef.current.documentId) {
        await updatePodcastListeningTime(lastSentTimeRef.current.documentId, {
          listeningTime: currentTime,
        });
        lastSentTimeRef.current.listeningTime = currentTime;
      }
    },
    [current, user, setListeningTime],
  );

  const handlePlayAudio = useCallback(() => {
    if (current) {
      trackGAEvent('podcast_played', {
        podcast_document_id: current.documentId ?? '',
        podcast_name: current.title ?? '',
      });
    }
  }, [current]);

  return {
    handleTimeUpdate,
    handlePlayAudio,
    listeningTime,
  };
};
